import { ref, type Ref } from 'vue'
import type { LocationQuery } from 'vue-router'
import { defineStore, storeToRefs } from 'pinia'
import {
    OrderShopware,
    PaymentMethodShopware,
    PaymentShippingShopware,
    SystemContextShopware,
    OpenAPI
} from '@hubblecommerce/hubble/platforms/shopware/api-client'
import type { HblIUseCheckout, HblPaymentMethod, HblShippingMethod } from '@/utils/types'
import { PAYMENT_HANDLE_FAILED } from '@/utils/helper/checkoutError'
import { reportCheckoutError } from '@/utils/sentry/reportCheckoutError'
import {
    useNotification,
    useCart,
    useRouter,
    hblMapPaymentMethods,
    hblMapShippingMethods,
    useLocalisation,
    useRequestURL,
    useFraudCheck
} from '#imports'

export const useCheckoutStore = defineStore('use-checkout', () => {
    const shippingError: Ref = ref(false)
    const paymentError: Ref = ref(false)
    const orderComment: Ref<string> = ref('')
    const orderAffiliateCode: Ref<string> = ref('')
    const orderCampaignCode: Ref<string> = ref('')
    const orderSubscription: Ref<boolean> = ref(false)
    const orderReferrer: Ref<string> = ref('')
    const shouldOpenPaymentModal: Ref<boolean> = ref(false)
    const isPaymentModalLockedByFraud: Ref<boolean> = ref(false)

    const triggerPaymentModal = () => {
        shouldOpenPaymentModal.value = true
        isPaymentModalLockedByFraud.value = true
    }

    const resetPaymentModalTrigger = () => {
        shouldOpenPaymentModal.value = false
        // Don't reset the lock here - only reset when modal is manually opened or payment method is changed
    }

    const unlockPaymentModal = () => {
        isPaymentModalLockedByFraud.value = false
    }

    return {
        shippingError,
        paymentError,
        orderComment,
        orderAffiliateCode,
        orderCampaignCode,
        orderSubscription,
        orderReferrer,
        shouldOpenPaymentModal,
        isPaymentModalLockedByFraud,
        triggerPaymentModal,
        resetPaymentModalTrigger,
        unlockPaymentModal
    }
})

export const useCheckout = function (): HblIUseCheckout {
    const error: Ref = ref(false)
    const loading: Ref<boolean> = ref(false)
    const shippingMethods: Ref<null | HblShippingMethod[]> = ref(null)
    const paymentMethods: Ref<null | HblPaymentMethod[]> = ref(null)
    const { deleteCart } = useCart()
    const { showNotification } = useNotification()
    const { currentRoute } = useRouter()
    const { navigateToI18n } = useLocalisation()
    const customerStore = useCustomer()
    const { customer } = storeToRefs(customerStore)

    const checkoutStore = useCheckoutStore()
    const { shippingError, paymentError, orderComment, orderAffiliateCode, orderCampaignCode, orderSubscription, orderReferrer, shouldOpenPaymentModal, isPaymentModalLockedByFraud } = storeToRefs(checkoutStore)
    const { triggerPaymentModal, resetPaymentModalTrigger, unlockPaymentModal } = checkoutStore

    function currentPaymentMethodCode (): string | undefined {
        const { session } = storeToRefs(usePlatform())
        return session?.value?.paymentMethod?.code
    }

    async function getShippingMethods (): Promise<HblShippingMethod[] | void> {
        loading.value = true
        error.value = false
        shippingError.value = false

        try {
            const response = await PaymentShippingShopware.readShippingMethod(
                true,
                {
                    associations: {
                        prices: {}
                    }
                }
            )

            if (typeof response?.elements === 'undefined') {
                return
            }

            const mappedData = hblMapShippingMethods(response?.elements)

            mappedData.sort((a, b) => {
                return a.position - b.position
            })

            shippingMethods.value = mappedData

            loading.value = false
            return mappedData
        } catch (e) {
            loading.value = false
            error.value = e
            shippingError.value = e
        }
    }

    async function setShippingMethod (id: string): Promise<void> {
        loading.value = true
        error.value = false
        shippingError.value = false

        try {
            await SystemContextShopware.updateContext(
                {
                    shippingMethodId: id
                }
            )

            loading.value = false
        } catch (e) {
            loading.value = false
            error.value = e
            shippingError.value = e
        }
    }

    async function getPaymentMethods (): Promise<HblPaymentMethod[] | void> {
        loading.value = true
        error.value = false
        paymentError.value = false

        try {
            const response = await PaymentMethodShopware.readPaymentMethod(
                {
                    onlyAvailable: true
                }
            )

            if (typeof response?.elements === 'undefined') {
                return
            }

            const mappedData = hblMapPaymentMethods(response?.elements)

            mappedData.sort((a, b) => {
                return a.position - b.position
            })

            paymentMethods.value = mappedData

            loading.value = false
            return mappedData
        } catch (e) {
            loading.value = false
            error.value = e
            paymentError.value = e
        }
    }

    async function setPaymentMethod (id: string): Promise<void> {
        loading.value = true
        error.value = false
        paymentError.value = false

        try {
            await SystemContextShopware.updateContext(
                {
                    paymentMethodId: id
                }
            )

            loading.value = false
        } catch (e) {
            loading.value = false
            error.value = e
            paymentError.value = e
        }
    }

    function validateCheckout (): boolean {
        if (shippingError.value) {
            showNotification('Something is wrong with your shipping, select a different shipping method and try again.', 'error', true)
            return false
        }

        if (paymentError.value) {
            showNotification('Something is wrong with your payment, select a different payment method and try again.', 'error', true)
            return false
        }

        return true
    }

    interface ValidationField {
        id: string
        errorMessage: string
        condition: boolean
    }

    async function placeOrder(): Promise<boolean | string> {
        loading.value = true
        error.value = false

        const platformStore = usePlatform()
        const { session } = storeToRefs(platformStore)

        const validationFields: ValidationField[] = [
            {
                id: "customer-iban",
                errorMessage: "IBAN-Kontonummer darf nicht leer sein.",
                // @ts-ignore
                condition: !customer?.value?.customerIban && Boolean(session?.value?.paymentMethod?.customFields?.custom_sepa_iban),
            },
            {
                id: "phoneNumber",
                errorMessage: "Telefonischer Kontakt darf nicht leer sein.",
                // @ts-ignore
                condition: !customer?.value?.billingAddress?.phone && Boolean(session?.value?.shippingMethod?.customFields?.phone_required),
            },
        ]

        for (const field of validationFields) {
            if (field.condition) {
                await validateField(field.id, field.errorMessage)
                loading.value = false
                return false
            }
        }

        try {
            // Fraud check for invoice payment only
            const { performFraudCheck, shouldPerformFraudCheck, shouldSkipFraudCheck } = useFraudCheck()

            // Skip fraud check if customer is excluded
            if (shouldSkipFraudCheck(customer.value)) {
                console.log('Customer excluded from CRIF check, proceeding with order')
            } else if (shouldPerformFraudCheck()) {
                const fraudCheckResult = await performFraudCheck()

                if (!fraudCheckResult.success) {
                    loading.value = false
                    error.value = fraudCheckResult.error || 'Payment verification failed'

                    // Trigger payment modal to open for checkout page
                    if (fraudCheckResult.blockedPaymentMethod) {
                        triggerPaymentModal()
                    }

                    return false
                }
            }

            const storedOrderSubscription = localStorage.getItem('orderSubscription');

            if (storedOrderSubscription) {
                orderSubscription.value = JSON.parse(storedOrderSubscription);
            }

            const response = await OrderShopware.createOrder(
                {
                    customerComment: orderComment.value,
                    affiliateCode: orderAffiliateCode.value,
                    campaignCode: orderCampaignCode.value,
                    // @ts-ignore
                    customFields: {
                        ...(orderSubscription.value && {custom_order_subscription: orderSubscription.value}),
                        referrer_url: orderReferrer.value,
                    },
                }
            )

            if (!response?.id) {
                error.value = new Error('Order creation returned no id')
                reportCheckoutError('placeOrder', error.value, { paymentMethodCode: currentPaymentMethodCode() })
                return false
            }

            await deleteCart()
            return response.id
        } catch (e) {
            error.value = e
            reportCheckoutError('placeOrder', e, { paymentMethodCode: currentPaymentMethodCode() })
            return false
        } finally {
            loading.value = false
        }
    }

    async function validateField(fieldId: string, errorMessage: string): Promise<void> {
        showNotification(errorMessage, "error")

        await nextTick()

        const field = document.getElementById(fieldId) as HTMLInputElement | null

        if (field) {
            field.setCustomValidity("Dieses Feld darf nicht leer sein.")
            field.reportValidity()
            field.scrollIntoView({ behavior: "smooth", block: "center" })

            field.addEventListener(
                "input",
                () => {
                    field.setCustomValidity("")
                },
                { once: true },
            )
        }
    }

    async function handlePayment (orderId: string, dataBag?: LocationQuery): Promise<void> {
        loading.value = true
        error.value = false

        if (dataBag === null) {
            dataBag = currentRoute.value.query
        }

        try {
            const response = await PaymentShippingShopware.handlePaymentMethod(
                {
                    orderId,
                    finishUrl: `${useRequestURL().origin}/checkout/success?orderId=${orderId}`,
                    errorUrl: `${useRequestURL().origin}/checkout/error?orderId=${orderId}`,
                    // TODO: patch api client
                    // @ts-ignore
                    ...(dataBag != null && { ...dataBag })
                }
            )

            if (response.redirectUrl !== null) {
                loading.value = false
                window.open(response.redirectUrl, '_self')
            } else {
                loading.value = false
                navigateToI18n(
                    {
                        path: '/checkout/success',
                        query: {
                            orderId
                        }
                    }
                )
            }
        } catch (e) {
            loading.value = false
            error.value = e
            reportCheckoutError('handlePayment', e, { paymentMethodCode: currentPaymentMethodCode() })

            navigateToI18n(
                {
                    path: '/checkout/error',
                    query: {
                        orderId,
                        'error-code': PAYMENT_HANDLE_FAILED
                    }
                }
            )
        }
    }

    async function resetPayment (orderId: string, paymentMethodId: string): Promise<boolean | void> {
        loading.value = true
        error.value = false

        try {
            const response = await OrderShopware.orderSetPayment(
                {
                    paymentMethodId,
                    orderId
                }
            )

            loading.value = false

            if (!response.success) {
                error.value = true
                return false
            }

            return response.success
        } catch (e) {
            loading.value = false
            error.value = e
            reportCheckoutError('resetPayment', e, { paymentMethodCode: currentPaymentMethodCode() })
        }
    }

    return {
        error,
        loading,
        shippingMethods,
        getShippingMethods,
        setShippingMethod,
        shippingError,
        paymentMethods,
        getPaymentMethods,
        setPaymentMethod,
        paymentError,
        placeOrder,
        validateCheckout,
        orderComment,
        orderAffiliateCode,
        orderCampaignCode,
        orderSubscription,
        orderReferrer,
        handlePayment,
        resetPayment,
        shouldOpenPaymentModal,
        isPaymentModalLockedByFraud,
        triggerPaymentModal,
        resetPaymentModalTrigger,
        unlockPaymentModal
    }
}
